import React, { memo, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { book } from '../../../../routers/books';
import { useAppSelector } from '../../../../store/hooks';
import { Hosts, getListingsPerPack, hasFreeListingBonus } from '../../../../constants/hostName';
import { useGetJobListingTypesQuery } from '../../../../services/servicePostAJob';
import { useGetJobPackagesQuery } from '../../../../services/servicePrepaidPurchase';

// Bulk-packages promo under the Step 1 card — the WHOLE strip is a router link
// to the Job Listing Packages flow (per-site route), led by a live "SAVE $X"
// badge computed from the real single-listing vs pack pricing (falls back to
// plain "SAVE" until both queries resolve).
const MultipleJobListing = () => {
  const [multipleJobsLink, setMultipleJobsLink] = useState<string>(book.jobs);
  const { siteNumber } = useAppSelector(({ pathnameSettings }) => ({
    siteNumber: pathnameSettings.siteNumber
  }));

  // Listings per pack — CPG Media 5, other brands 6 (5 purchased + 1 free). Mirrors
  // the PrepaidPurchase page; the API response carries only per-pack price.
  const packListings = getListingsPerPack(siteNumber);

  // Both queries are RTK-cached (Step 1 already fetches listing types; the
  // packages query warms the cache for the packages page the strip links to).
  const { data: listingTypes } = useGetJobListingTypesQuery({
    id: siteNumber.toString()
  }, { skip: !siteNumber });
  const { data: jobPackages } = useGetJobPackagesQuery({
    id: siteNumber.toString()
  }, { skip: !siteNumber });

  // Percent saved vs buying `packListings` singles, floored to the nearest 5 so the
  // "N%+" claim always understates (e.g. $699 pack vs 6 × $149 = 21.8% → "Save 20%+").
  // Null until both prices load.
  const savingsPct = useMemo(() => {
    const standardPack = jobPackages?.prepaidPackages?.find(
      (p) => /standard/i.test(p.description)
    );
    const singlePrice = listingTypes?.standardPrice;
    if (
      standardPack
      && typeof standardPack.itemPrice === 'number'
      && typeof singlePrice === 'number'
      && Number.isFinite(singlePrice)
      && singlePrice > 0
    ) {
      const exact = (1 - standardPack.itemPrice / (packListings * singlePrice)) * 100;
      const pct = Math.floor(exact / 5) * 5;
      return pct >= 5 ? pct : null;
    }
    return null;
  }, [jobPackages, listingTypes, packListings]);

  useEffect(() => {
    if (siteNumber) {
      switch (siteNumber) {
        case Hosts.NOSH:
          setMultipleJobsLink(book.jobsNosh);
          break;
        case Hosts.BREWBOUND:
          setMultipleJobsLink(book.jobsBrewbound);
          break;
        default:
          setMultipleJobsLink(book.jobs);
      }
    }
  }, [siteNumber]);

  return (
    <div className="cpg-promo-strip">
      <Link
        to={multipleJobsLink}
        className="cpg-strip cpg-strip--cta"
      >
        <span className="cpg-strip__lead cpg-strip__lead--save">
          {savingsPct ? `Save ${savingsPct}%+` : 'Save'}
        </span>
        <span className="cpg-strip__copy">
          <b>Hiring for more than one role?</b>
          {' '}
          {hasFreeListingBonus(siteNumber)
            ? `Get ${packListings} listings for the price of 5 with a job listing pack`
            : `Get ${packListings} listings with a job listing pack`}
          {', valid for 24 months and shareable across your team.'}
        </span>
        <span className="cpg-strip__go">View packages →</span>
      </Link>
    </div>
  );
};

export default memo(MultipleJobListing);
